// Supplier Management
import { useState, useCallback, useMemo } from "react";
import { Factory, Plus, Pencil, AlertCircle } from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import AdminLayout from "@/components/admin/AdminLayout";
import { AdminDataTable } from "@/components/admin/shared/AdminDataTable";
import type { ColumnDef } from "@/components/admin/shared/AdminDataTable";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { SearchInput } from "@/components/admin/shared/SearchInput";
import { FilterBar } from "@/components/admin/shared/FilterBar";
import { EmptyState } from "@/components/admin/shared/EmptyState";
import { useSuppliers, useCreateSupplier } from "@/hooks/admin/useB2B";
import type {
  Supplier,
  SupplierStatus,
  CreateSupplierInput,
} from "@/lib/admin-api/b2b";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SENEGAL_REGIONS = [
  "Dakar",
  "Kaolack",
  "Saint-Louis",
  "Fatick",
  "Thiès-Mbour",
  "Diourbel-Touba",
] as const;

const EMPTY_FORM: CreateSupplierInput = {
  name: "",
  contact: "",
  phone: "",
  email: "",
  region: "Dakar",
  status: "active",
};

// ---------------------------------------------------------------------------
// Supplier form modal
// ---------------------------------------------------------------------------

interface SupplierFormModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onSave: (input: CreateSupplierInput) => Promise<void>;
  readonly isSaving: boolean;
}

function SupplierFormModal({
  open,
  onClose,
  onSave,
  isSaving,
}: SupplierFormModalProps) {
  const [form, setForm] = useState<CreateSupplierInput>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    setForm(EMPTY_FORM);
    setError(null);
    onClose();
  }, [onClose]);

  const update = useCallback(
    <K extends keyof CreateSupplierInput>(
      key: K,
      value: CreateSupplierInput[K],
    ) => {
      setForm((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const handleSubmit = useCallback(async () => {
    if (!form.name.trim()) {
      setError("Le nom est requis.");
      return;
    }
    if (!form.phone.trim()) {
      setError("Le téléphone est requis.");
      return;
    }
    setError(null);
    try {
      await onSave(form);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Une erreur est survenue.");
    }
  }, [form, onSave, handleClose]);

  const inputClass =
    "h-10 w-full rounded-md border border-stone-200 px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100";
  const labelClass = "mb-1.5 block text-sm font-medium text-stone-700";

  return (
    <AdminModal
      open={open}
      onClose={handleClose}
      title="Nouveau fournisseur"
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={handleClose} disabled={isSaving}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving} className="gap-1.5">
            {isSaving && (
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            )}
            Créer
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label className={labelClass}>Nom du fournisseur</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            placeholder="Nom"
            className={inputClass}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>Contact</label>
            <input
              type="text"
              value={form.contact}
              onChange={(e) => update("contact", e.target.value)}
              placeholder="Nom du contact"
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Téléphone</label>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => update("phone", e.target.value)}
              placeholder="+221 7X XXX XX XX"
              className={inputClass}
            />
          </div>
        </div>
        <div>
          <label className={labelClass}>Email</label>
          <input
            type="email"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            placeholder="fournisseur@exemple.com"
            className={inputClass}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>Région</label>
            <select
              value={form.region}
              onChange={(e) => update("region", e.target.value)}
              className={inputClass}
            >
              {SENEGAL_REGIONS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Statut</label>
            <select
              value={form.status}
              onChange={(e) => update("status", e.target.value as SupplierStatus)}
              className={inputClass}
            >
              <option value="active">Actif</option>
              <option value="inactive">Inactif</option>
            </select>
          </div>
        </div>
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
      </div>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export default function Suppliers() {
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<Record<string, string | null>>({
    status: null,
    region: null,
  });
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const params = useMemo(
    () => ({
      search: search || undefined,
      status: (filters.status as SupplierStatus) ?? undefined,
      region: filters.region ?? undefined,
    }),
    [search, filters],
  );

  const { data, isLoading, isError, refetch } = useSuppliers(params);
  const createSupplier = useCreateSupplier();

  const suppliers = data?.suppliers ?? [];

  const handleFilterChange = useCallback((key: string, value: unknown) => {
    setFilters((prev) => ({ ...prev, [key]: value as string | null }));
  }, []);

  const handleSave = useCallback(
    async (input: CreateSupplierInput) => {
      await createSupplier.mutateAsync(input);
      setIsCreateOpen(false);
    },
    [createSupplier],
  );

  const filterConfigs = useMemo(
    () => [
      {
        key: "status",
        label: "Statut",
        type: "select" as const,
        options: [
          { value: "active", label: "Actif" },
          { value: "inactive", label: "Inactif" },
        ],
      },
      {
        key: "region",
        label: "Région",
        type: "select" as const,
        options: SENEGAL_REGIONS.map((r) => ({ value: r, label: r })),
      },
    ],
    [],
  );

  const columns: readonly ColumnDef<Supplier>[] = useMemo(
    () => [
      {
        id: "name",
        header: "Fournisseur",
        cell: (s) => (
          <span className="font-medium text-stone-900">{s.name}</span>
        ),
      },
      {
        id: "contact",
        header: "Contact",
        cell: (s) => (
          <span className="text-sm text-stone-600">{s.contact || "—"}</span>
        ),
      },
      {
        id: "phone",
        header: "Téléphone",
        cell: (s) => (
          <span className="text-sm text-stone-600">{s.phone}</span>
        ),
      },
      {
        id: "region",
        header: "Région",
        cell: (s) => (
          <span className="rounded-full bg-stone-100 px-2 py-0.5 text-xs text-stone-700">
            {s.region}
          </span>
        ),
      },
      {
        id: "product_count",
        header: "Produits",
        cell: (s) => (
          <span className="text-sm text-stone-600">{s.product_count}</span>
        ),
      },
      {
        id: "last_order_at",
        header: "Dernière commande",
        cell: (s) => (
          <span className="text-sm text-stone-500">
            {s.last_order_at
              ? new Date(s.last_order_at).toLocaleDateString("fr-SN")
              : "—"}
          </span>
        ),
      },
      {
        id: "status",
        header: "Statut",
        cell: (s) => (
          <span
            className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
              s.status === "active"
                ? "bg-emerald-100 text-emerald-700"
                : "bg-stone-100 text-stone-500"
            }`}
          >
            {s.status === "active" ? "Actif" : "Inactif"}
          </span>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        cell: () => (
          <div className="flex items-center gap-1">
            <button
              type="button"
              className="rounded p-1.5 text-stone-400 hover:bg-stone-100 hover:text-stone-700"
              aria-label="Modifier"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
              <Factory className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">
                Fournisseurs
              </h1>
              <p className="text-sm text-stone-500">
                {isLoading
                  ? "Chargement…"
                  : `${suppliers.length} fournisseur(s)`}
              </p>
            </div>
          </div>
          <Button onClick={() => setIsCreateOpen(true)} className="gap-1.5">
            <Plus className="h-4 w-4" />
            Nouveau fournisseur
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Rechercher par nom ou contact…"
          />
          <FilterBar
            filters={filterConfigs}
            values={filters}
            onChange={handleFilterChange}
          />
        </div>

        {/* Error */}
        {isError && (
          <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>Impossible de charger les fournisseurs.</span>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              Réessayer
            </Button>
          </div>
        )}

        {/* Table */}
        {!isError && (
          <>
            {!isLoading && suppliers.length === 0 ? (
              <EmptyState
                icon={<Factory className="h-6 w-6" />}
                title="Aucun fournisseur"
                description="Créez votre premier fournisseur."
                action={{
                  label: "Nouveau fournisseur",
                  onClick: () => setIsCreateOpen(true),
                }}
              />
            ) : (
              <AdminDataTable
                data={suppliers}
                columns={columns}
                isLoading={isLoading}
              />
            )}
          </>
        )}
      </div>

      <SupplierFormModal
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onSave={handleSave}
        isSaving={createSupplier.isPending}
      />
    </AdminLayout>
  );
}
